import {
  AwardIcon,
  BriefcaseBusinessIcon,
  GraduationCapIcon,
  Loader2Icon,
  TextIcon,
} from "lucide-react";

import { OverwriteProfileButton } from "./OverwriteProfileButton";
import {
  Column,
  LaborupGear,
  RichTextDisplay,
  Row,
  SheetSectionContainer,
  SheetSectionHeader,
  Text,
} from "@/components";
import { CertificationsView } from "@/components/certifications/CertificationsView";
import { EducationsView } from "@/components/educations/EducationsView";
import { SkillsView } from "@/components/skills/SkillsView";
import { WorkExperiencesView } from "@/components/user-sheet/profile-editors/WorkExperiencesView";

import type { EnrichmentCandidacy } from "./types";

import { laborupGearColor } from "@/lib/constants";
import { cn } from "@/lib/utils";
import {
  CertificationType,
  EducationType,
  EnrichedProfileState,
  EnrichmentCertification,
  EnrichmentEducation,
  EnrichmentMessage,
  EnrichmentMessageRole,
  EnrichmentResponse,
  EnrichmentWorkExperience,
  SkillType,
  WorkExperienceType,
} from "@/types";

// Mappers — enrichment shapes don't carry DB ids or every extra field the
// candidate-sheet components track (internalCompany, etc.). We shim with
// dummy ids and undefined for unused fields.
const mapWorkExperiences = (
  items: EnrichmentWorkExperience[],
): WorkExperienceType[] =>
  items.map(
    (we, idx) =>
      ({
        id: idx,
        ...we,
        internalCompany: undefined,
      }) as unknown as WorkExperienceType,
  );

const mapEducations = (items: EnrichmentEducation[]): EducationType[] =>
  items.map(
    (ed, idx) =>
      ({
        id: idx,
        ...ed,
      }) as unknown as EducationType,
  );

const mapCertifications = (
  items: EnrichmentCertification[],
): CertificationType[] =>
  items.map(
    (c, idx) =>
      ({
        id: idx,
        ...c,
      }) as unknown as CertificationType,
  );

const mapSkills = (skills: string[]): SkillType[] =>
  skills.map(
    (name, idx) =>
      ({
        id: idx,
        name,
      }) as unknown as SkillType,
  );

const ExecutiveSummaryCard = ({ summary }: { summary: string }) => {
  return (
    <Column className="gap-4 rounded-lg border border-primary-300 bg-primary-200 p-4">
      <Row className="items-center gap-2">
        <LaborupGear color={laborupGearColor} height={24} width={24} />
        <p className="text-lg font-semibold">Executive Summary</p>
      </Row>
      <RichTextDisplay richText={summary} />
    </Column>
  );
};

const AssistantBubble = ({ response }: { response: EnrichmentResponse }) => {
  return (
    <Column className="w-full max-w-full gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <Text className="whitespace-pre-wrap text-sm text-slate-700">
        {response.preamble}
      </Text>

      {response.executiveSummary !== null && (
        <ExecutiveSummaryCard summary={response.executiveSummary} />
      )}

      {response.professionalSummary !== null && (
        <SheetSectionContainer>
          <SheetSectionHeader icon={TextIcon} title="Professional Summary" />
          <Text className="text-sm leading-relaxed text-slate-700">
            {response.professionalSummary}
          </Text>
        </SheetSectionContainer>
      )}

      {response.workExperiences !== null && (
        <SheetSectionContainer>
          <SheetSectionHeader
            icon={BriefcaseBusinessIcon}
            title="Work Experience"
          />
          <WorkExperiencesView
            workExperiences={mapWorkExperiences(response.workExperiences)}
          />
        </SheetSectionContainer>
      )}

      {/* SkillsView wraps itself in SheetSectionContainer + SkillsWrapper, so it
          already provides the divider, padding, and section header. Empty array is
          the default "clear all skills" response — not worth showing. */}
      {response.skills !== null && response.skills.length > 0 && (
        <SkillsView skills={mapSkills(response.skills)} />
      )}

      {response.certifications !== null && (
        <SheetSectionContainer>
          <SheetSectionHeader
            icon={AwardIcon}
            title="Certifications / Licenses"
          />
          <CertificationsView
            certifications={mapCertifications(response.certifications)}
          />
        </SheetSectionContainer>
      )}

      {response.educations !== null && (
        <SheetSectionContainer>
          <SheetSectionHeader icon={GraduationCapIcon} title="Education" />
          <EducationsView educations={mapEducations(response.educations)} />
        </SheetSectionContainer>
      )}
    </Column>
  );
};

export const EnrichmentMessages = ({
  candidacy,
  enrichedProfile,
  isEnriching,
  messages,
  onApplied,
}: {
  candidacy: EnrichmentCandidacy;
  enrichedProfile: EnrichedProfileState;
  isEnriching: boolean;
  messages: EnrichmentMessage[];
  onApplied: () => void;
}) => {
  // Find the last message sent and determine if it was the AI assistant's
  const lastMessage = messages.at(-1);
  const lastAssistantId =
    lastMessage?.role === EnrichmentMessageRole.ASSISTANT
      ? lastMessage.id
      : null;

  /* Render */
  const render = () => {
    if (messages.length === 0 && !isEnriching) return null;
    return (
      <Column className="gap-4 pt-2">
        {messages.map((message) => (
          <Column className="gap-3" key={message.id}>
            <Row
              className={cn(
                "w-full",
                message.role === EnrichmentMessageRole.USER
                  ? "justify-end"
                  : "justify-start",
              )}
            >
              {message.role === EnrichmentMessageRole.USER ? (
                <div className="max-w-[85%] rounded-2xl bg-primary-900 px-4 py-3 text-sm text-white shadow-sm">
                  <Text className="whitespace-pre-wrap break-words text-sm text-white">
                    {message.content}
                  </Text>
                </div>
              ) : (
                <div className="w-full max-w-[95%]">
                  <AssistantBubble
                    response={JSON.parse(message.content) as EnrichmentResponse}
                  />
                </div>
              )}
            </Row>
            {message.role === EnrichmentMessageRole.ASSISTANT &&
              message.id === lastAssistantId &&
              !isEnriching && (
                <OverwriteProfileButton
                  candidacy={candidacy}
                  enrichedProfile={enrichedProfile}
                  isEnriching={isEnriching}
                  onApplied={onApplied}
                />
              )}
          </Column>
        ))}
        {isEnriching && (
          <Row className="items-center gap-2 text-sm text-slate-500">
            <Loader2Icon className="h-4 w-4 animate-spin text-primary-900" />
            <Text className="text-sm" variant="muted">
              Enriching candidate profile…
            </Text>
          </Row>
        )}
      </Column>
    );
  };

  return render();
};
